import React, { useEffect, useState, useCallback } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import {
  messList,
  messAttendanceSheet,
  messMarkAttendance,
  messAttendanceCheckout,
} from "../../api/Mess";
import MessLayout from "./MessLayout";

const MEAL_TYPES = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "full_day", label: "Full Day" },
];

const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString() : "-");

export default function MessAttendanceMark() {
  const [loading, setLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState({}); // enrollment_id -> bool, disables just that row's buttons
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().slice(0, 10),
    meal_type: "full_day",
    type: "Student",
  });
  const [messes, setMesses] = useState([]);

  const getMessList = async () => {
    setLoading(true);
    try {
      const response = await messList(`&limit=all`);
      if (response.data?.status) {
        setMesses(response?.data?.data);
        setFilters((prev) => ({
          ...prev,
          mess_id: response?.data?.data?.[0]?.id,
        }));
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getSheet = useCallback(async () => {
    if (
      !filters?.mess_id ||
      !filters?.type ||
      !filters?.date ||
      !filters?.meal_type
    )
      return;
    setLoading(true);
    try {
      const response = await messAttendanceSheet({
        mess_id: filters.mess_id,
        type: filters.type,
        date: filters.date,
        meal_type: filters.meal_type,
      });
      if (response.data?.status) {
        // status stays exactly as the server returns it: null = not yet
        // marked. We never pre-fill "present" here.
        setRows(response?.data?.data ?? []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    getMessList();
  }, []);

  useEffect(() => {
    getSheet();
  }, [getSheet]);

  const withRowBusy = async (enrollmentId, fn) => {
    setRowBusy((prev) => ({ ...prev, [enrollmentId]: true }));
    try {
      await fn();
    } finally {
      setRowBusy((prev) => ({ ...prev, [enrollmentId]: false }));
    }
  };

  // Marks a single person present, the moment they arrive. No effect on
  // anyone else's row.
  const checkIn = (enrollmentId) =>
    withRowBusy(enrollmentId, async () => {
      try {
        const response = await messMarkAttendance({
          mess_id: filters.mess_id,
          date: filters.date,
          meal_type: filters.meal_type,
          attendances: [{ enrollment_id: enrollmentId, status: "present" }],
        });
        if (response.data?.status) {
          await getSheet(); // pulls back the real check_in_time + attendance_id
        } else {
          SweetAlertShow("info", "Warning", response?.data?.message);
        }
      } catch (error) {
        SweetAlertShow(
          "error",
          "Error",
          error?.response?.data?.message ??
            error?.response?.message ??
            error?.message,
        );
      }
    });

  // Explicitly marks one person absent (e.g. staff knows in advance they
  // won't show up). Separate action from check-in, never a default.
  const markAbsent = (enrollmentId) =>
    withRowBusy(enrollmentId, async () => {
      try {
        const response = await messMarkAttendance({
          mess_id: filters.mess_id,
          date: filters.date,
          meal_type: filters.meal_type,
          attendances: [{ enrollment_id: enrollmentId, status: "absent" }],
        });
        if (response.data?.status) {
          await getSheet();
        } else {
          SweetAlertShow("info", "Warning", response?.data?.message);
        }
      } catch (error) {
        SweetAlertShow(
          "error",
          "Error",
          error?.response?.data?.message ??
            error?.response?.message ??
            error?.message,
        );
      }
    });

  const checkOut = (row) =>
    withRowBusy(row.enrollment_id, async () => {
      try {
        const response = await messAttendanceCheckout({
          id: row.attendance_id,
        });
        if (response.data?.status) {
          await getSheet();
        } else {
          SweetAlertShow("info", "Warning", response?.data?.message);
        }
      } catch (error) {
        SweetAlertShow(
          "error",
          "Error",
          error?.response?.data?.message ??
            error?.response?.message ??
            error?.message,
        );
      }
    });

  // Deliberate end-of-window closeout: only touches people who are still
  // unmarked, never overwrites an existing present/absent. Requires
  // confirmation since it's a bulk action.
  const closeOutRemaining = async () => {
    const unmarked = rows.filter((r) => r.status === null);
    if (!unmarked.length) return;
    const ok = window.confirm(
      `Mark ${unmarked.length} unmarked ${filters.type.toLowerCase()}(s) as absent for this ${filters.meal_type.replace("_", " ")}? This can still be undone per person afterwards.`,
    );
    if (!ok) return;

    setLoading(true);
    try {
      const response = await messMarkAttendance({
        mess_id: filters.mess_id,
        date: filters.date,
        meal_type: filters.meal_type,
        attendances: unmarked.map((r) => ({
          enrollment_id: r.enrollment_id,
          status: "absent",
        })),
      });
      if (response.data?.status) {
        SweetAlertShow("success", "Success", response?.data?.message);
        getSheet();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const presentCount = rows.filter((r) => r.status === "present").length;
  const absentCount = rows.filter((r) => r.status === "absent").length;
  const unmarkedCount = rows.filter((r) => r.status === null).length;

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row py-5">
                  <MessLayout />
                  <div className="col-12">
                    <div className="card my-3">
                      <div className="card-body">
                        <h4 className="text-primary mb-0">
                          Mark Mess Attendance
                        </h4>
                        <hr />
                        <div className="row align-items-center">
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters?.mess_id ?? ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    mess_id: e.target.value,
                                  })
                                }
                              >
                                {messes?.map((mess) => (
                                  <option key={mess.id} value={mess.id}>
                                    {mess.name}
                                  </option>
                                ))}
                              </select>
                              <label>Select Mess</label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters?.type}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    type: e.target.value,
                                  })
                                }
                              >
                                <option value="Student">Student</option>
                                <option value="Staff">Staff</option>
                                <option value="Guest">Guest</option>
                              </select>
                              <label>Select Type</label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                className="form-control"
                                value={filters?.date}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    date: e.target.value,
                                  })
                                }
                              />
                              <label>Date</label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters?.meal_type}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    meal_type: e.target.value,
                                  })
                                }
                              >
                                {MEAL_TYPES.map((m) => (
                                  <option key={m.value} value={m.value}>
                                    {m.label}
                                  </option>
                                ))}
                              </select>
                              <label>Meal</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <span className="text-muted">
                            {presentCount} checked in &middot; {absentCount}{" "}
                            absent &middot; {unmarkedCount} not yet marked
                          </span>
                          <button
                            className="btn btn-outline-danger"
                            disabled={!unmarkedCount}
                            onClick={closeOutRemaining}
                          >
                            Mark Remaining Absent ({unmarkedCount})
                          </button>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-bordered align-middle">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Mobile</th>
                                <th>Status</th>
                                <th>Check In</th>
                                <th>Check Out</th>
                                <th className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => {
                                const busy = !!rowBusy[r.enrollment_id];
                                return (
                                  <tr key={r.enrollment_id}>
                                    <td>{i + 1}</td>
                                    <td>{r.name}</td>
                                    <td>{r.mobile}</td>
                                    <td>
                                      {r.status === "present" && (
                                        <span className="badge bg-success">
                                          Present
                                        </span>
                                      )}
                                      {r.status === "absent" && (
                                        <span className="badge bg-danger">
                                          Absent
                                        </span>
                                      )}
                                      {r.status === null && (
                                        <span className="badge bg-secondary">
                                          Not Marked
                                        </span>
                                      )}
                                    </td>
                                    <td>{fmtTime(r.check_in_time)}</td>
                                    <td>{fmtTime(r.check_out_time)}</td>
                                    
                                    <td className="text-center">
                                      <div className="d-flex gap-1 justify-content-center flex-wrap">
                                        {r.status !== "present" && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-success"
                                            disabled={busy}
                                            onClick={() =>
                                              checkIn(r.enrollment_id)
                                            }
                                          >
                                            Check In
                                          </button>
                                        )}
                                        {r.status === "present" &&
                                          !r.check_out_time && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              disabled={busy}
                                              onClick={() => checkOut(r)}
                                            >
                                              Check Out
                                            </button>
                                          )}
                                        {r.status !== "absent" && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            disabled={busy}
                                            onClick={() =>
                                              markAbsent(r.enrollment_id)
                                            }
                                          >
                                            Mark Absent
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {!rows.length && (
                                <tr>
                                  <td
                                    colSpan={7}
                                    className="text-center text-muted"
                                  >
                                    No active enrollments for this mess/type.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
